import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { addmmeberNoteApi, getmemberProgrammingNotes } from "src/DAL/NotesApi/Notes";
import { getReviewNotesApi, saveNoteItemsApi, saveReviewNoteApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, Panel, Pill, TextLink } from "./cwUi";
import LightEditor from "./LightEditor";

/**
 * P3.10 + P4.2 — coach notebook (design PDF page 2 right column, page 7
 * on the feedback page). Coach only, auto-saves. Layout as the PDF:
 * "Last review · date" on top, "This review" below, Hide; history and the
 * general notepad behind links.
 *   This review  — note + next steps (tick) for THIS check-in
 *   Last review  — the note of the person's previous check-in; its next
 *                  steps can be ticked here
 *   History      — older review notes with dates
 *   General      — the SAME member notepad as Member profile → Notepad
 *                  (api/notepad/get_notes + addMemberNote), unchanged
 */

const SAVE_AFTER_MS = 1200;

function savedLabel(state, at) {
  if (state === "saving") return "Saving…";
  if (state === "error") return "Not saved — check connection";
  if (state === "loading") return "Loading…";
  return at ? `Saved ${moment.min(moment(at), moment()).fromNow()}` : "Auto-saves as you type";
}

/** Tickable next steps. editable = add / remove; onChange(items). */
function ItemList({ items, onChange, editable, testId }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...(items || []), { text: t, done: false }]);
    setText("");
  };
  return (
    <Box data-testid={testId}>
      {(items || []).map((item, i) => (
        <Box key={item.id || `n${i}`} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, py: 0.25 }}>
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={Boolean(item.done)}
            disabled={!onChange}
            onChange={(e) => onChange && onChange(items.map((it, j) => (j === i ? { ...it, done: e.target.checked } : it)))}
            inputProps={{ "aria-label": item.text }}
          />
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.6 }}>
            <Typography sx={{ fontSize: 13.5, color: item.done ? CW.muted : CW.ink, textDecoration: item.done ? "line-through" : "none" }}>
              {item.text}
            </Typography>
            {item.done && item.doneAt ? (
              <Typography sx={{ fontSize: 11.5, color: CW.muted }}>
                Done {moment(item.doneAt).format("D MMM")}
                {item.doneByName ? ` · ${item.doneByName}` : ""}
              </Typography>
            ) : null}
          </Box>
          {editable ? (
            <IconButton size="small" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Box>
      ))}
      {editable ? (
        <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="Add a next step…"
            maxLength={500}
            style={{ flex: 1, minWidth: 0, border: `1px solid ${CW.line}`, borderRadius: 8, padding: "6px 10px", fontSize: 13.5 }}
          />
          <button type="button" className="small-contained-outlined-button" style={{ fontSize: 12, padding: "3px 10px" }} onClick={add}>
            Add
          </button>
        </Box>
      ) : null}
    </Box>
  );
}

function NoteView({ note }) {
  return (
    <Box>
      {note.content ? (
        <Box
          sx={{ fontSize: 13.5, color: CW.ink, "& p": { m: 0 }, wordBreak: "break-word", mb: 1 }}
          dangerouslySetInnerHTML={{ __html: note.content }}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: CW.muted, mb: 1 }}>No written note.</Typography>
      )}
    </Box>
  );
}

function GeneralNotes({ memberId }) {
  const editorRef = useRef(null);
  const timerRef = useRef(null);
  const lastSavedRef = useRef("");
  const [state, setState] = useState("loading");
  const [savedAt, setSavedAt] = useState(null);
  const [html, setHtml] = useState(null);

  useEffect(() => {
    let live = true;
    getmemberProgrammingNotes(memberId).then((result) => {
      if (!live) return;
      const content = result?.code === 200 ? result?.notes?.content || "" : "";
      lastSavedRef.current = content;
      setHtml(content);
      setSavedAt(result?.notes?.updatedAt || null);
      setState(result?.code === 200 ? "idle" : "error");
    });
    return () => {
      live = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [memberId]);

  const save = async () => {
    const content = editorRef.current ? editorRef.current.getHtml() : "";
    if (content === lastSavedRef.current) return;
    setState("saving");
    const result = await addmmeberNoteApi({ userId: memberId, content });
    if (result?.code === 200) {
      lastSavedRef.current = content;
      setSavedAt(new Date());
      setState("saved");
    } else setState("error");
  };

  if (html === null) return <CircularProgress size={22} />;
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: CW.muted, mb: 1 }}>Shared with Member profile → Notepad.</Typography>
      <LightEditor
        ref={editorRef}
        initialHtml={html}
        testId="coach-notebook-general"
        placeholder="General notes about this client…"
        onChange={() => {
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(save, SAVE_AFTER_MS);
        }}
      />
      <Typography sx={{ fontSize: 12, color: state === "error" ? CW.red : CW.muted, mt: 0.75 }}>{savedLabel(state, savedAt)}</Typography>
    </Box>
  );
}

export default function CoachNotebook({ memberId, submissionId, variant = "review", onOpenFeedback, footer, sx }) {
  const [view, setView] = useState(submissionId ? "main" : "general");
  const [hidden, setHidden] = useState(false);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [savedAt, setSavedAt] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const editorRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!submissionId) return;
    setState("loading");
    const result = await getReviewNotesApi(submissionId);
    if (result?.code === 200) {
      setData(result);
      setItems(result.current?.items || []);
      setSavedAt(result.current?.updatedAt || null);
      setState("idle");
    } else setState("error");
  }, [submissionId]);

  useEffect(() => {
    setData(null);
    load();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [load]);

  const saveThis = async (patch) => {
    setState("saving");
    const result = await saveReviewNoteApi(submissionId, patch);
    if (result?.code === 200) {
      setSavedAt(new Date());
      setState("saved");
      if (patch.items) setItems(result.note.items);
      setData((d) => (d ? { ...d, current: result.note } : d));
    } else setState("error");
  };

  const onTextChange = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveThis({ content: editorRef.current ? editorRef.current.getHtml() : "" }), SAVE_AFTER_MS);
  };

  const onItemsChange = (next) => {
    setItems(next);
    saveThis({ items: next });
  };

  const tickLast = async (next) => {
    const last = data?.last;
    if (!last) return;
    setData((d) => ({ ...d, last: { ...d.last, items: next } }));
    const result = await saveNoteItemsApi(last.id, next);
    if (result?.code === 200) setData((d) => ({ ...d, last: result.note }));
  };

  const copyOpenItems = () => {
    const openItems = (data?.last?.items || []).filter((it) => !it.done).map((it) => ({ text: it.text, done: false }));
    const have = new Set(items.map((it) => it.text));
    onItemsChange([...items, ...openItems.filter((it) => !have.has(it.text))]);
  };

  const lastOpen = (data?.last?.items || []).filter((it) => !it.done).length;
  const sectionLabel = { fontSize: 13.5, fontWeight: 700, color: CW.ink, mb: 1 };
  const back = (
    <TextLink onClick={() => setView("main")} sx={{ fontSize: 13, mb: 1.25, display: "inline-block" }}>
      ‹ Back to this review
    </TextLink>
  );

  const thisReview = !data ? (
    state === "error" ? (
      <Typography sx={{ color: CW.red, fontSize: 13 }}>Notes did not load.</Typography>
    ) : (
      <CircularProgress size={22} />
    )
  ) : (
    <Box>
      <Typography sx={sectionLabel}>
        This review{variant === "feedback" ? " · Same notes as the review page" : ""}
      </Typography>
      {variant === "feedback" ? <ItemList items={items} onChange={onItemsChange} editable testId="notebook-items" /> : null}
      <Box sx={{ bgcolor: CW.soft, border: `1px solid ${CW.line}`, borderRadius: "10px", p: 1, mt: variant === "feedback" ? 1 : 0 }}>
        <LightEditor
          ref={editorRef}
          initialHtml={data.current?.content || ""}
          onChange={onTextChange}
          testId="coach-notebook"
          minHeight={variant === "feedback" ? 70 : 120}
          placeholder="What you noticed in this check-in…"
        />
        <Typography sx={{ fontSize: 12, color: state === "error" ? CW.red : CW.muted, mt: 0.75, px: 0.5 }}>
          {savedLabel(state, savedAt)}
        </Typography>
      </Box>
      {variant !== "feedback" ? (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: CW.muted, mt: 1.5, mb: 0.25, letterSpacing: 0.4 }}>NEXT STEPS</Typography>
          <ItemList items={items} onChange={onItemsChange} editable testId="notebook-items" />
        </>
      ) : null}
    </Box>
  );

  const lastReview = data?.last ? (
    <Box sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${CW.line}` }}>
      <Typography sx={sectionLabel}>Last review · {moment(data.last.submittedAt).format("D MMMM")}</Typography>
      <Box sx={{ fontSize: 14.5, lineHeight: 1.6, color: CW.ink }}>
        <NoteView note={data.last} />
      </Box>
      {(data.last.items || []).length ? (
        <>
          <ItemList items={data.last.items} onChange={tickLast} editable={false} testId="last-items" />
          {lastOpen ? (
            <TextLink onClick={copyOpenItems} sx={{ fontSize: 12.5, mt: 0.75 }}>
              Carry {lastOpen} open step{lastOpen === 1 ? "" : "s"} to this review
            </TextLink>
          ) : null}
        </>
      ) : null}
    </Box>
  ) : null;

  return (
    <Panel title="Coach notebook" info={variant === "feedback" ? "feedback.notebook" : "review.notebook"} sx={sx}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: -0.5, mb: 2 }}>
        <Pill sx={{ fontSize: 13, px: 1.5, py: 0.6 }}>Coach only</Pill>
        {variant === "review" ? (
          <TextLink onClick={() => setHidden((h) => !h)} sx={{ fontSize: 13.5 }}>
            {hidden ? "Show" : "Hide"}
          </TextLink>
        ) : null}
      </Box>

      {hidden ? (
        <Typography sx={{ fontSize: 13, color: CW.muted }}>Notes hidden. They still save.</Typography>
      ) : (
        <>
          {view === "main" && submissionId ? (
            <>
              {variant === "review" ? lastReview : null}
              {thisReview}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
                <TextLink onClick={() => setView("history")} sx={{ fontSize: 12.5, fontWeight: 600 }}>
                  See dated history
                </TextLink>
                {memberId ? (
                  <TextLink onClick={() => setView("general")} sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    General notes
                  </TextLink>
                ) : null}
                {variant === "feedback" && data?.last ? (
                  <TextLink onClick={() => setView("last")} sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    Last review
                  </TextLink>
                ) : null}
              </Box>
              {footer || null}
              {onOpenFeedback ? (
                <TextLink onClick={onOpenFeedback} sx={{ mt: 2.5, display: "inline-block" }}>
                  Same notebook in feedback →
                </TextLink>
              ) : null}
            </>
          ) : null}

          {view === "last" ? (
            <>
              {back}
              {lastReview || <Typography sx={{ fontSize: 13, color: CW.muted }}>No note on the previous check-in.</Typography>}
            </>
          ) : null}

          {view === "history" ? (
            <>
              {back}
              {!(data?.history || []).length ? (
                <Typography sx={{ fontSize: 13, color: CW.muted }}>No older review notes.</Typography>
              ) : (
                data.history.map((note) => {
                  const done = (note.items || []).filter((it) => it.done).length;
                  const isOpen = open === note.id;
                  return (
                    <Box key={note.id} sx={{ borderTop: `1px solid ${CW.line}`, py: 1 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpen(isOpen ? null : note.id)}
                        sx={{ all: "unset", cursor: "pointer", display: "flex", gap: 1, alignItems: "center", width: "100%" }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink }}>
                          {moment(note.submittedAt).format("D MMM YYYY")}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: CW.muted, flex: 1 }}>{note.byName}</Typography>
                        {(note.items || []).length ? (
                          <Pill tone={done === note.items.length ? "green" : "muted"}>
                            {done}/{note.items.length}
                          </Pill>
                        ) : null}
                      </Box>
                      {isOpen ? (
                        <Box sx={{ mt: 1 }}>
                          <NoteView note={note} />
                          <ItemList items={note.items} onChange={null} editable={false} />
                        </Box>
                      ) : null}
                    </Box>
                  );
                })
              )}
            </>
          ) : null}

          {view === "general" ? (
            <>
              {submissionId ? back : null}
              {memberId ? (
                <GeneralNotes memberId={memberId} />
              ) : (
                <Typography sx={{ fontSize: 13, color: CW.muted }}>General notes need a member account.</Typography>
              )}
            </>
          ) : null}
        </>
      )}
    </Panel>
  );
}
